import { allVerdiSchemas, type VerdiSchema, type VerdiSchemaKey } from "../index.js";
import { type RemoveArgsFromFunctionLookup } from "./types.js";
import type { GettersForKey } from "./schemaRegistryTypes.generated.js";

/**
 * For some reason, having a fixed registry of all the getters means that the
 * Typescript doesn't explode. If we do it with just types, it can only deal with a couple dozen schemas.
 */
const verdiGetterRegistry = {
    ...Object.fromEntries(Object.entries(allVerdiSchemas).map(([_k, v]) => [v.schemaKey, v.getters])),
} as GettersForKey;

/**
 * Returns all getters for the model that you give it, modified so they can be called without providing the
 * model as an argument (to reduce code duplication).
 *
 * e.g. findGetters(myDevice).shortCode()
 *
 * (If this function isn't showing any getters for a model your giving it, make sure the model is in the index.ts and
 * has a distinct modelType that is set "as const")
 * @param model
 * @returns {RemoveArgsFromFunctionLookup<{}[InputModel["schemaKey"]]["getters"]>}
 */
export function findGetters<K extends VerdiSchemaKey>(
    model: { schemaKey: K } & VerdiSchema,
): RemoveArgsFromFunctionLookup<(typeof verdiGetterRegistry)[K]> {
    /**
     * Explaining how this works here so I don't pollute the docstring comment.
     *
     * This function is a bit black magic. What it actually does is pretty straightforward. It finds
     * the model class for the model that it receives and then gives you the associated getters with the arguments removed
     * for easier use.
     *
     * The tricky part is the typing. Getting type hinting to just generically work on this was fairly tough. But it relies heavily on
     * a lookup table of all models by modelType and the modelType being typed to have a specific distinct value for each class.
     *
     * I tried to reduce how much stuff you have to do to create new models, but could not shrink the explicit typing very much.
     * You need to explicitly set the modelType and add it to the lookup table in order for a model to be "registered" and
     * run properly in this. I could find no other way around it.
     */
    return Object.fromEntries(
        Object.entries(allVerdiSchemas[model.schemaKey].getters).map(([key, value]) => [key, () => value(model)]),
    ) as RemoveArgsFromFunctionLookup<(typeof verdiGetterRegistry)[K]>;
}
